import {
    BioLibApiError,
    BioLibAuthenticationError,
    BioLibPermissionError,
    BioLibSingleton,
    IFilesDict,
    IJob,
    IJobPost,
    NetworkConnectionLost,
    JobUuid,
} from '@biolibtech/biolib-js';
import { call, fork, put, race, select, take, takeLatest } from 'redux-saga/effects';
import config from '../../config';
import { IRootState } from '../../state/rootReducer';
import userConstants from '../../state/user/userConstants';
import { handleError } from '@utils';
import appRunSlice from './appRunSlice';
import {
    ErrorReportSendingState,
    IJobWrapper,
    JobStatus,
} from './types';
import {
    isOAuthReAuthenticationRequiredError,
    startReAuthentication,
} from '../../apiHooks/startReAuthentication';
import getSortedAppVersion from './utils/getSortedAppVersion';
import prepareParametersAndFiles from './utils/prepareParametersAndFiles';
import { Action } from 'redux';
import { dispatch } from '../../state';
import { IStartJobArgs, jobsApi } from '../../apiHooks/jobsApi';
import { resultsApi } from '../../apiHooks/resultApi';
import { parseApiErrorMessage } from '../../apiHooks/utils';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';

function* watchOpenApp() {
    yield takeLatest(appRunSlice.actions.openApp.type, function* (action) {
        if (appRunSlice.actions.openApp.match(action)) {
            try {
                const { app, selectedAppVersion, openInModal } = action.payload;
                const isSignedIn: boolean = yield select((state: IRootState) => state.user.isSignedIn);
                const hasAcceptedTerms: boolean =
                    yield select((state: IRootState) => state.appRun.hasGuestUserAcceptedTerms);

                if (!isSignedIn && !hasAcceptedTerms && !config.isTeamSubdomainPage()) {
                    yield put(appRunSlice.actions.openAppAwaitingAcceptTerms(action.payload));

                    yield race([
                        take(appRunSlice.actions.setHasGuestAcceptedTermsToTrue),
                        take(userConstants.SIGN_IN_SUCCESS),
                    ]);
                }

                const biolib = BioLibSingleton.get();
                const jobPostData: IJobPost = {
                    app_resource_name_prefix: app.resource_name_prefix,
                    app_version_id: selectedAppVersion.public_id,
                    client_type: 'biolib-frontend',
                    client_version: config.frontendVersion,
                };
                const job: IJob = yield biolib.job.create(jobPostData);

                const appVersionIdOnPage: string | undefined =
                    yield select((state: IRootState) => state.appPage.appVersionSelectedId);
                if (!openInModal && appVersionIdOnPage !== selectedAppVersion.public_id) {
                    // The user navigated to another app or version while the job was being created
                    yield put(appRunSlice.actions.quitAppAwaitingAcceptTerms());
                    yield put(appRunSlice.actions.openAppCancelled());
                    return;
                }

                yield put(appRunSlice.actions.openAppSuccess({
                    openInModal,
                    jobWrapper: {
                        app,
                        error: null,
                        errorReportSendingState: ErrorReportSendingState.notInitiated,
                        job: { ...job, app_version: getSortedAppVersion(job.app_version) },
                        logMessages: [],
                        streamedOutput: '',
                        progressCompute: 0,
                        progressInitialization: 0,
                        status: JobStatus.input,
                    }
                }));

                if (!isSignedIn && !hasAcceptedTerms) {
                    yield put(appRunSlice.actions.quitAppAwaitingAcceptTerms());
                }

            } catch (error) {
                if (isOAuthReAuthenticationRequiredError(error)) {
                    let isReAuthenticating = false;
                    try {
                        isReAuthenticating = yield call(startReAuthentication);
                    } catch (reAuthenticationError) {
                        console.error('Failed to start re-authentication:', reAuthenticationError);
                    }
                    if (isReAuthenticating) {
                        yield put(appRunSlice.actions.openAppFailure(
                            'Redirecting to re-authenticate via your organization\'s identity provider...',
                        ));
                        return;
                    }
                }

                if (error instanceof BioLibAuthenticationError || error instanceof BioLibPermissionError) {
                    yield call(handleError, {
                        error: new Error('You do not have permission to run this application.'),
                        actionToDispatch: appRunSlice.actions.openAppFailure,
                        friendlyErrorMessageToPrepend: '',
                        showToast: true,
                    });
                } else {
                    yield call(handleError, {
                        error,
                        actionToDispatch: appRunSlice.actions.openAppFailure,
                        friendlyErrorMessageToPrepend: 'Unable to open application',
                        showToast: true,
                    });
                }
            }
        }
    });
}

async function startJob(args: IStartJobArgs): Promise<void> {
    const mutation = dispatch(jobsApi.endpoints.startJob.initiate(args));
    try {
        await mutation.unwrap();
    } catch (error) {
        const { status, data } = error as FetchBaseQueryError;
        if (status === 'FETCH_ERROR' || status === 'TIMEOUT_ERROR') {
            throw new NetworkConnectionLost('Network Error: Lost Connection to Server');
        }
        throw BioLibApiError.generate({
            statusText: parseApiErrorMessage(error as FetchBaseQueryError),
            status: typeof status === 'number' ? status : undefined,
            data: data as Record<string, string>,
        });
    } finally {
        mutation.reset();
    }
}

function* watchRunJob() {
    yield takeLatest(appRunSlice.actions.runJob.type, function* (action) {
        if (appRunSlice.actions.runJob.match(action)) {
            const { jobId, settingValues } = action.payload;
            try {
                const jobWrapper: IJobWrapper = yield select((state: IRootState) => state.appRun.jobWrapperDict[jobId]);
                const appVersion = jobWrapper.job.app_version;

                const isSignedIn: boolean = yield select((state: IRootState) => state.user.isSignedIn);
                const url = isSignedIn
                    ? `/results/${jobWrapper.job.public_id}/`
                    : `/results/${jobWrapper.job.public_id}/?token=${jobWrapper.job.auth_token}`;
                if (typeof window !== 'undefined') {
                    window.history.replaceState({ reloadOnPopState: true }, '', location.pathname);
                    window.history.pushState({}, '', url);
                }

                const files: IFilesDict = {};
                const parameters: string[] = [];
                yield prepareParametersAndFiles(appVersion.settings, settingValues, files, parameters);

                const { quitJobAction } = yield race({
                    quitJobAction: take((action: Action) =>
                        appRunSlice.actions.quitJob.match(action) && action.payload.jobId === jobId
                    ),
                    runJobResult: call(startJob, {
                        job: jobWrapper.job,
                        moduleInput: { files, parameters },
                    }),
                });

                if (quitJobAction) {
                    const cancelMutation = dispatch(resultsApi.endpoints.cancelResult.initiate({
                        jobUuid: jobId,
                        jobAuthToken: jobWrapper.job.auth_token,
                    }));
                    cancelMutation.then(() => cancelMutation.reset());
                } else {
                    yield put(appRunSlice.actions.runJobSuccess({ jobId }));
                }
            } catch (error) {
                yield put(appRunSlice.actions.runJobFailure({
                    jobId,
                    error,
                    status: JobStatus.error,
                }));
            }
        }
    });
}

export default function* appRunSaga() {
    yield fork(watchOpenApp);
    yield fork(watchRunJob);
}
